export const TERMINAL_FULFILLMENTS = ['EFFECTUATED', 'NOOP_TERMINAL'];

export const CANCEL_DATA_RETENTION_MESSAGE = 'Ao cancelar, tenant, usuarios e dados operacionais permanecem preservados conforme politica de retencao e auditoria.';

export const normalizeFulfillment = (value) => {
  const normalized = String(value || '').trim().toUpperCase();
  if (!normalized) {
    return null;
  }
  return normalized;
};

const resolveLegacyOperationalStage = (item) => {
  const requestStatus = String(item?.status || '').trim().toUpperCase();
  const paymentState = String(item?.payment_state || '').trim().toLowerCase();
  const chargeStatus = String(item?.charge_status || '').trim().toLowerCase();
  const prepStatus = String(item?.payment_preparation_status || '').trim().toLowerCase();

  if (requestStatus === 'PENDING') return 'pending_approval';
  if (requestStatus !== 'APPROVED') return 'not_applicable';

  if (chargeStatus === 'paid' || paymentState === 'paid') return 'active_after_webhook_compatible';
  if (
    prepStatus === 'failed' ||
    chargeStatus === 'failed' ||
    chargeStatus === 'cancelled' ||
    chargeStatus === 'refunded' ||
    paymentState === 'payment_setup_failed' ||
    paymentState === 'canceled'
  ) {
    return 'payment_failed';
  }
  if (chargeStatus === 'pending' || chargeStatus === 'overdue' || paymentState === 'pending_payment') {
    return 'payment_pending';
  }
  if (prepStatus === 'prepared' || prepStatus === 'reused' || item?.payment_intent_id || item?.charge_id) {
    return 'payment_prepared';
  }
  return 'approved_awaiting_payment';
};

export const resolveOperationalStage = (item) => {
  const requestStatus = String(item?.status || '').trim().toUpperCase();
  const fulfillment = normalizeFulfillment(item?.fulfillment);
  if (fulfillment) {
    if (fulfillment === 'NOT_STARTED') {
      if (requestStatus === 'PENDING') return { stage: 'pending_approval', source: 'fulfillment', fulfillment };
      if (requestStatus === 'APPROVED') return { stage: 'approved_awaiting_payment', source: 'fulfillment', fulfillment };
      return { stage: 'not_applicable', source: 'fulfillment', fulfillment };
    }
    if (fulfillment === 'PAYMENT_IN_PROGRESS') {
      return { stage: 'approved_payment_in_progress', source: 'fulfillment', fulfillment };
    }
    if (fulfillment === 'PAID_PENDING_APPLY') {
      return { stage: 'paid_pending_apply', source: 'fulfillment', fulfillment };
    }
    if (fulfillment === 'EFFECTUATED') {
      return { stage: 'effectuated', source: 'fulfillment', fulfillment };
    }
    if (fulfillment === 'FAILED') {
      return { stage: 'payment_failed', source: 'fulfillment', fulfillment };
    }
    if (fulfillment === 'NOOP_TERMINAL') {
      return { stage: 'noop_terminal', source: 'fulfillment', fulfillment };
    }
  }
  return { stage: resolveLegacyOperationalStage(item), source: 'legacy', fulfillment: null };
};

export const isRequestOpenForUpgrade = (item) => {
  const type = String(item?.request_type || '').trim().toUpperCase();
  if (type !== 'UPGRADE') {
    return false;
  }
  const status = String(item?.status || '').trim().toUpperCase();
  const fulfillment = normalizeFulfillment(item?.fulfillment);
  const isTerminal = TERMINAL_FULFILLMENTS.includes(String(fulfillment || ''));
  return (status === 'PENDING' || status === 'APPROVED') && !isTerminal;
};

const normalizeRequestType = (value) => String(value || '').trim().toUpperCase();
const normalizeRequestStatus = (value) => String(value || '').trim().toUpperCase();

const isOpenNonTerminalRequest = (item) => {
  const status = normalizeRequestStatus(item?.status);
  const fulfillment = normalizeFulfillment(item?.fulfillment);
  const isTerminal = TERMINAL_FULFILLMENTS.includes(String(fulfillment || ''));
  return (status === 'PENDING' || status === 'APPROVED') && !isTerminal;
};

export const isScheduledCancelRequest = (item) => {
  const requestType = normalizeRequestType(item?.request_type);
  const status = normalizeRequestStatus(item?.status);
  const fulfillment = normalizeFulfillment(item?.fulfillment);
  const hasScheduledDate = Boolean(item?.scheduled_effective_on);

  return requestType === 'CANCEL' && status === 'APPROVED' && fulfillment === 'NOT_STARTED' && hasScheduledDate;
};

export const isRevocableScheduledCancelRequest = (item) => {
  if (!isScheduledCancelRequest(item)) {
    return false;
  }

  const status = normalizeRequestStatus(item?.status);
  const fulfillment = normalizeFulfillment(item?.fulfillment);
  const hasProcessedAt = Boolean(item?.scheduled_processed_at);

  if (status === 'CANCELLED' || status === 'REJECTED' || fulfillment === 'EFFECTUATED') {
    return false;
  }

  return !hasProcessedAt;
};

export const findActiveScheduledCancelRequest = (changeRequests = []) => {
  const rows = Array.isArray(changeRequests) ? changeRequests : [];
  return rows.find((item) => isRevocableScheduledCancelRequest(item)) || null;
};

export const resolveCancelEligibility = (subscription, changeRequests = []) => {
  const rows = Array.isArray(changeRequests) ? changeRequests : [];
  const hasSubscription = Boolean(subscription?.has_subscription);
  const subscriptionStatus = String(subscription?.status || '').trim().toLowerCase();
  const eligibleStatuses = ['active', 'trialing', 'suspended'];

  if (!hasSubscription) {
    return {
      canCancel: false,
      cancelReason: 'Sem assinatura ativa para cancelar no momento.',
      retentionNotice: CANCEL_DATA_RETENTION_MESSAGE,
    };
  }

  if (subscriptionStatus === 'cancelled') {
    return {
      canCancel: false,
      cancelReason: 'Assinatura ja esta cancelada. Nao e possivel solicitar novo cancelamento.',
      retentionNotice: 'Assinatura cancelada. Os dados permanecem preservados conforme politica de retencao e auditoria.',
    };
  }

  if (!eligibleStatuses.includes(subscriptionStatus)) {
    return {
      canCancel: false,
      cancelReason: 'Status atual da assinatura nao permite cancelamento self-service neste momento.',
      retentionNotice: CANCEL_DATA_RETENTION_MESSAGE,
    };
  }

  const activeScheduledCancel = findActiveScheduledCancelRequest(rows);
  if (activeScheduledCancel) {
    return {
      canCancel: false,
      cancelReason: activeScheduledCancel.scheduled_effective_on
        ? `Ja existe cancelamento agendado para ${activeScheduledCancel.scheduled_effective_on}.`
        : 'Ja existe cancelamento agendado em andamento.',
      retentionNotice: CANCEL_DATA_RETENTION_MESSAGE,
    };
  }

  const conflictingRequest = rows.find((item) => {
    if (!isOpenNonTerminalRequest(item)) {
      return false;
    }
    return normalizeRequestType(item?.request_type) !== 'CANCEL';
  });

  if (conflictingRequest) {
    return {
      canCancel: false,
      cancelReason: `Existe solicitacao em andamento (${conflictingRequest.id || 'sem id'}) que precisa ser concluida antes do cancelamento.`,
      retentionNotice: CANCEL_DATA_RETENTION_MESSAGE,
    };
  }

  return {
    canCancel: true,
    cancelReason: '',
    retentionNotice: CANCEL_DATA_RETENTION_MESSAGE,
  };
};

export const getRequestCreationAvailability = (
  subscription,
  requests = [],
  { upgradePlans = [], downgradePlans = [] } = {}
) => {
  const blockingUpgrade = (Array.isArray(requests) ? requests : []).find((item) => isRequestOpenForUpgrade(item));
  const hasUpgradePlan = Array.isArray(upgradePlans) && upgradePlans.length > 0;
  const hasDowngradePlan = Array.isArray(downgradePlans) && downgradePlans.length > 0;
  const cancelEligibility = resolveCancelEligibility(subscription, requests);

  return {
    canUpgrade: !blockingUpgrade && hasUpgradePlan,
    upgradeReason: blockingUpgrade
      ? `Ja existe upgrade em andamento (${blockingUpgrade.id}). Acompanhe o ciclo atual antes de abrir nova solicitacao.`
      : hasUpgradePlan
        ? ''
        : 'Nao ha planos elegiveis para upgrade no momento.',
    canDowngrade: hasDowngradePlan,
    downgradeReason: hasDowngradePlan ? '' : 'Nao ha planos elegiveis para downgrade no momento.',
    canCancel: cancelEligibility.canCancel,
    cancelReason: cancelEligibility.cancelReason,
    cancelRetentionNotice: cancelEligibility.retentionNotice,
  };
};

export const mapSubmitErrorToUserMessage = (detail, fallback) => {
  const normalized = String(detail || '').toLowerCase();
  if (!normalized) {
    return fallback;
  }

  if (
    normalized.includes('already') ||
    normalized.includes('exist') ||
    normalized.includes('duplic') ||
    normalized.includes('conflit') ||
    normalized.includes('em aberto')
  ) {
    return 'Voce ja possui uma solicitacao em andamento. Acompanhe o ciclo atual para evitar duplicidade.';
  }

  if (
    (normalized.includes('plan') && normalized.includes('invalid')) ||
    normalized.includes('plano solicitado nao encontrado') ||
    normalized.includes('plano solicitado nao esta ativo')
  ) {
    return 'O plano selecionado nao esta mais disponivel para upgrade. Atualize a lista e tente novamente.';
  }

  return detail || fallback;
};

export const resolveCreateRequestSuccessFeedback = (item) => {
  const status = String(item?.status || '').trim().toUpperCase();
  const fulfillment = normalizeFulfillment(item?.fulfillment);
  const requestType = String(item?.request_type || '').trim().toUpperCase();

  if (requestType === 'DOWNGRADE' && status === 'APPROVED' && fulfillment === 'NOT_STARTED') {
    return {
      message: 'Downgrade agendado para o proximo ciclo.',
      description: item?.scheduled_effective_on
        ? `Seu plano atual permanece ativo ate ${item.scheduled_effective_on}.`
        : 'Seu plano atual permanece ativo ate o proximo ciclo.',
    };
  }

  if (status === 'APPROVED' && fulfillment === 'PAYMENT_IN_PROGRESS') {
    return {
      message: 'Upgrade iniciado. Finalize o pagamento para concluir a mudanca do plano.',
      description: 'A solicitacao foi aprovada automaticamente e a cobranca foi preparada.',
    };
  }

  if (status === 'PENDING') {
    return {
      message: 'Solicitacao enviada para analise. Acompanhe o ciclo operacional abaixo.',
      description: 'A solicitacao foi registrada e sera analisada manualmente pela plataforma.',
    };
  }

  return {
    message: 'Solicitacao registrada. Acompanhe o ciclo operacional abaixo.',
    description: 'A solicitacao foi registrada com sucesso.',
  };
};

export const buildTenantLifecycleSteps = (item, paymentInfo) => {
  const stage = resolveOperationalStage(item).stage;
  const requestStatus = String(item?.status || '').trim().toUpperCase();
  const paymentState = String(paymentInfo?.payment_state || item?.payment_state || '').trim().toLowerCase();

  const steps = [
    {
      key: 'requested',
      title: 'Solicitacao criada',
      done: true,
      description: 'Solicitacao registrada no autosservico.',
    },
    {
      key: 'approved',
      title: 'Aprovacao comercial',
      done: requestStatus === 'APPROVED' || requestStatus === 'REJECTED',
      error: requestStatus === 'REJECTED',
      description:
        requestStatus === 'PENDING'
          ? 'Aguardando aprovacao da plataforma.'
          : requestStatus === 'REJECTED'
            ? 'Solicitacao rejeitada pela plataforma.'
            : 'Solicitacao aprovada.',
    },
    {
      key: 'payment',
      title: 'Ciclo de pagamento',
      done: ['paid'].includes(paymentState) || ['paid_pending_apply', 'effectuated'].includes(stage),
      error: ['failed', 'payment_setup_failed', 'canceled', 'cancelled'].includes(paymentState) || stage === 'payment_failed',
      description:
        stage === 'approved_payment_in_progress'
          ? 'Pagamento em processamento.'
          : stage === 'paid_pending_apply'
            ? 'Pagamento confirmado; aguardando aplicacao do plano.'
            : stage === 'effectuated'
              ? 'Pagamento concluido e ciclo fechado.'
              : stage === 'payment_failed'
                ? 'Falha no fluxo financeiro; acompanhamento manual necessario.'
                : 'Pagamento aguardando preparo/confirmacao.',
    },
    {
      key: 'effectuation',
      title: 'Efetivacao do plano',
      done: stage === 'effectuated',
      error: stage === 'noop_terminal',
      description:
        stage === 'effectuated'
          ? 'Upgrade efetivado com sucesso.'
          : stage === 'noop_terminal'
            ? 'Solicitacao encerrada sem aplicacao (no-op terminal).'
            : 'Aguardando efetivacao apos ciclo financeiro.',
    },
  ];

  return steps;
};
